/**
 * Transposition utilities using Tonal.js
 *
 * Provides functions for transposing notes, validating flute range,
 * and working with musical intervals.
 */

import { Note, Interval } from "tonal";

// Flute range: C4 to C7 (MIDI 60 to 96)
const FLUTE_MIN_MIDI = 60; // C4
const FLUTE_MAX_MIDI = 96; // C7

/**
 * Parse a note string and get its MIDI number
 * @param note Note string (e.g., "C5", "F#4", "Bb6")
 * @returns MIDI number or null if invalid
 */
export function noteToMidi(note: string): number | null {
    const parsed = Note.get(note);
    return parsed.midi ?? null;
}

/**
 * Convert MIDI number to note name
 * @param midi MIDI number (0-127)
 * @param preferSharps If true, use sharps instead of flats (default: true)
 * @returns Note name (e.g., "C5", "F#4")
 */
export function midiToNote(midi: number, preferSharps: boolean = true): string {
    const note = Note.fromMidi(midi);
    if (!preferSharps && note.includes("#")) {
        // Convert sharp to flat enharmonic
        const flattened = Note.enharmonic(note);
        return flattened;
    }
    return note;
}

/**
 * Transpose a note by a number of semitones
 * @param note Note string (e.g., "C5", "F#4")
 * @param semitones Number of semitones to transpose (positive = up, negative = down)
 * @returns Transposed note or empty string if invalid
 *
 * @example
 * transpose("C5", 2)   // "D5" (up a major second)
 * transpose("C5", -3)  // "A4" (down a minor third)
 * transpose("F#5", 5)  // "B5" (up a perfect fourth)
 */
export function transpose(note: string, semitones: number): string {
    if (semitones === 0) return note;

    const interval = Interval.fromSemitones(semitones);
    const transposed = Note.transpose(note, interval);
    return transposed || "";
}

/**
 * Check if a note is within the flute's playable range (C4-C7)
 * @param note Note string (e.g., "C5", "F#4")
 * @returns True if note is in range, false otherwise
 */
export function isInFluteRange(note: string): boolean {
    const midi = noteToMidi(note);
    if (midi === null) return false;
    return midi >= FLUTE_MIN_MIDI && midi <= FLUTE_MAX_MIDI;
}

/**
 * Clamp a note to the flute's playable range (C4-C7)
 * If the note is below C4, returns C4
 * If the note is above C7, returns C7
 * Otherwise returns the note unchanged
 *
 * @param note Note string
 * @returns Note clamped to flute range
 */
export function clampToFluteRange(note: string): string {
    const midi = noteToMidi(note);
    if (midi === null) return note; // Return original if invalid

    if (midi < FLUTE_MIN_MIDI) {
        return midiToNote(FLUTE_MIN_MIDI);
    }
    if (midi > FLUTE_MAX_MIDI) {
        return midiToNote(FLUTE_MAX_MIDI);
    }
    return note;
}

/**
 * Transpose an array of notes by the same number of semitones
 * @param notes Array of note strings
 * @param semitones Number of semitones to transpose
 * @param clampToRange If true, clamp out-of-range notes to C4-C7 (default: false)
 * @returns Array of transposed notes
 *
 * @example
 * transposeNotes(["C5", "D5", "E5"], 2)  // ["D5", "E5", "F#5"]
 * transposeNotes(["C5", "D5", "E5"], 2, true)  // Clamps if needed
 */
export function transposeNotes(
    notes: string[],
    semitones: number,
    clampToRange: boolean = false
): string[] {
    return notes.map(note => {
        const transposed = transpose(note, semitones);
        if (clampToRange && transposed) {
            return clampToFluteRange(transposed);
        }
        return transposed || note; // Return original if transposition failed
    });
}

/**
 * Get the name of a key based on transposition from C
 * @param semitones Semitones from C (0 = C, 2 = D, -3 = A, etc.)
 * @returns Key name (e.g., "C", "D", "Eb")
 */
export function getKeyName(semitones: number): string {
    // Normalize to 0-11 range
    const normalized = ((semitones % 12) + 12) % 12;
    const keyNote = transpose("C4", normalized);
    // Remove octave number
    return keyNote.replace(/\d+$/, "");
}

/**
 * Get flute range information
 * @returns Object with min and max notes and MIDI numbers
 */
export function getFluteRange() {
    return {
        minNote: midiToNote(FLUTE_MIN_MIDI),
        maxNote: midiToNote(FLUTE_MAX_MIDI),
        minMidi: FLUTE_MIN_MIDI,
        maxMidi: FLUTE_MAX_MIDI,
    };
}
